package com.shadowings.gero.presentation.screen

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.ListItem
import androidx.compose.material3.ListItemDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.QuizQuestionType
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.domain.model.WrongAnswerRecord
import com.shadowings.gero.presentation.component.GeroButton
import com.shadowings.gero.presentation.component.MdCWord
import com.shadowings.gero.presentation.component.quiz.AlynFeedbackBox
import com.shadowings.gero.presentation.ui.AppTheme
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.errori_correggi_btn
import gero.composeapp.generated.resources.errori_empty_state
import gero.composeapp.generated.resources.errori_ready_state
import gero.composeapp.generated.resources.errori_ready_state_one
import gero.composeapp.generated.resources.quiz_type_image_keyboard
import gero.composeapp.generated.resources.quiz_type_multi_press
import gero.composeapp.generated.resources.quiz_type_multi_selection
import gero.composeapp.generated.resources.quiz_type_phonetic_keyboard
import gero.composeapp.generated.resources.quiz_type_single_selection
import gero.composeapp.generated.resources.quiz_type_text_translation
import gero.composeapp.generated.resources.quiz_type_true_false
import gero.composeapp.generated.resources.quiz_type_word_selection
import gero.composeapp.generated.resources.quiz_type_word_spelling
import org.jetbrains.compose.resources.stringResource
import org.jetbrains.compose.resources.painterResource
import com.shadowings.gero.data.repository.InscriptionImages
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.draw.clip
import androidx.compose.ui.Alignment
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.Image

@Composable
private fun alynFaceAndText(wrongAnswersCount: Int): Pair<Faces, String> = when {
    wrongAnswersCount == 0 -> Faces.Happy to stringResource(Res.string.errori_empty_state)
    wrongAnswersCount == 1 -> Faces.Serious to stringResource(Res.string.errori_ready_state_one)
    else -> Faces.Serious to stringResource(Res.string.errori_ready_state, wrongAnswersCount)
}

@Composable
private fun quizTypeLabel(type: QuizQuestionType): String = when (type) {
    QuizQuestionType.TRUE_FALSE -> stringResource(Res.string.quiz_type_true_false)
    QuizQuestionType.SINGLE_SELECTION -> stringResource(Res.string.quiz_type_single_selection)
    QuizQuestionType.MULTI_SELECTION -> stringResource(Res.string.quiz_type_multi_selection)
    QuizQuestionType.MULTI_PRESS -> stringResource(Res.string.quiz_type_multi_press)
    QuizQuestionType.WORD_SELECTION -> stringResource(Res.string.quiz_type_word_selection)
    QuizQuestionType.TEXT_TRANSLATION -> stringResource(Res.string.quiz_type_text_translation)
    QuizQuestionType.WORD_SPELLING -> stringResource(Res.string.quiz_type_word_spelling)
    QuizQuestionType.PHONETIC_KEYBOARD -> stringResource(Res.string.quiz_type_phonetic_keyboard)
    QuizQuestionType.IMAGE_KEYBOARD -> stringResource(Res.string.quiz_type_image_keyboard)
}

@Composable
fun ErroriListContent(
    userData: UserData,
    wrongAnswers: List<WrongAnswerRecord>,
    modifier: Modifier = Modifier,
    onCorreggiClick: () -> Unit = {}
) {
    Column(modifier = modifier) {
        val (face, feedbackText) = alynFaceAndText(wrongAnswers.size)
        AlynFeedbackBox(
            face = face,
            text = feedbackText,
            userData = userData,
            interactive = true
        )
        GeroButton(
            enabled = wrongAnswers.isNotEmpty(),
            onClick = onCorreggiClick,
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp)
                .semantics { testTag = "errori_correggi_btn" },
        ) {
            Text(text = stringResource(Res.string.errori_correggi_btn))
        }

        LazyColumn(
            modifier = Modifier
                .fillMaxSize()
                .weight(1f)
                .semantics { testTag = "errori_list" },
            contentPadding = PaddingValues(horizontal = 16.dp, vertical = 8.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            items(wrongAnswers, key = { it.id }) { mistake ->
                ErroreCard(mistake)
            }
        }
    }
}

@Composable
private fun ErroreCard(mistake: WrongAnswerRecord) {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .semantics { testTag = "errore_card_${mistake.id}" },
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant),
        border = BorderStroke(2.dp, MaterialTheme.colorScheme.outlineVariant)
    ) {
        ListItem(
            colors = ListItemDefaults.colors(containerColor = Color.Transparent),
            headlineContent = {
                Text(
                    modifier = Modifier.fillMaxWidth(),
                    text = quizTypeLabel(mistake.type)
                )
            },
            supportingContent = {
                val inscription = if (mistake.type == QuizQuestionType.IMAGE_KEYBOARD) {
                    InscriptionImages.resFromCode(mistake.questionText)
                } else null
                if (inscription != null) {
                    Box(modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
                        Image(
                            modifier = Modifier
                                .height(48.dp)
                                .clip(RoundedCornerShape(8.dp)),
                            painter = painterResource(inscription),
                            contentDescription = null,
                            contentScale = ContentScale.Fit
                        )
                    }
                } else if (mistake.hieroglyphs.isNotBlank()) {
                    MdCWord(word = mistake.hieroglyphs, rtl = false, cart = false, size = 32.dp)
                } else {
                    Text(
                        modifier = Modifier.fillMaxWidth(),
                        textAlign = TextAlign.Center,
                        text = mistake.questionText,
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis
                    )
                }
            }
        )
    }
}

internal val previewWrongAnswers = listOf(
    WrongAnswerRecord(
        id = "1",
        lessonId = "uni_1",
        type = QuizQuestionType.SINGLE_SELECTION,
        questionText = "Cosa significa questo geroglifico?",
        correctAnswerText = "Uomo",
        userAnswerText = "Donna",
        hieroglyphs = "A1"
    ),
    WrongAnswerRecord(
        id = "2",
        lessonId = "uni_2",
        type = QuizQuestionType.TRUE_FALSE,
        questionText = "Questo geroglifico rappresenta il sole?",
        hieroglyphs = "N5"
    ),
    WrongAnswerRecord(
        id = "3",
        lessonId = "word_selection_1",
        type = QuizQuestionType.WORD_SELECTION,
        questionText = "Componi la frase corretta",
        hieroglyphs = ""
    )
)

@Preview
@Composable
private fun ErroriListContentEmptyPreview() {
    AppTheme {
        Surface {
            ErroriListContent(wrongAnswers = emptyList(), userData = UserData())
        }
    }
}

@Preview
@Composable
private fun ErroriListContentSinglePreview() {
    AppTheme {
        Surface {
            ErroriListContent(wrongAnswers = previewWrongAnswers.take(1), userData = UserData())
        }
    }
}

@Preview
@Composable
private fun ErroriListContentReadyPreview() {
    AppTheme {
        Surface {
            ErroriListContent(wrongAnswers = previewWrongAnswers, userData = UserData())
        }
    }
}
